'use client';

import { cn } from '@/app/lib/utils';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getActiveTourismCategory, tourismNavItems } from './tourism-nav.config';

type TourismBottomNavProps = {
  upcomingEventsCount?: number;
};

export function TourismBottomNav({ upcomingEventsCount = 0 }: TourismBottomNavProps) {
  const pathname = usePathname();
  const activeCategory = useMemo(() => getActiveTourismCategory(pathname), [pathname]);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      if (currentY < 24) {
        setIsHidden(false);
        lastScrollY.current = currentY;
        return;
      }

      if (diff > 12) setIsHidden(true); // скролл вниз — прячем
      if (diff < -12) setIsHidden(false); // скролл вверх — показываем

      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      aria-label="Быстрая мобильная навигация по туризму"
      className={cn(
        'fixed inset-x-3 bottom-3 z-40 rounded-2xl border border-slate-700/60 bg-slate-950/90 p-2 shadow-2xl backdrop-blur-md transition-all duration-300 xl:hidden',
        isHidden ? 'translate-y-24 opacity-0' : 'translate-y-0 opacity-100',
      )}
    >
      <div className="grid grid-cols-4 gap-1">
        {tourismNavItems.map((item) => {
          const isActive = item.value === activeCategory;
          const showEventsBadge = item.value === 'events' && upcomingEventsCount > 0;
          const badgeText = upcomingEventsCount > 99 ? '99+' : String(upcomingEventsCount);

          return (
            <Link
              key={item.value}
              href={item.href}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex min-h-14 flex-col items-center justify-center rounded-xl px-1 text-center transition-all',
                isActive
                  ? 'bg-sky-500/20 text-sky-100'
                  : 'text-slate-300 hover:bg-slate-800/80 hover:text-white',
              )}
            >
              <motion.div whileTap={{ scale: 0.92 }} className="flex flex-col items-center">
                <div className="relative">
                  <Icon
                    icon={item.icon}
                    style={{ color: isActive ? '#7DD3FC' : item.color }}
                    className={cn(
                      'h-5 w-5 transition-transform duration-200',
                      isActive ? 'scale-110' : 'scale-100',
                    )}
                    aria-hidden="true"
                  />
                  {showEventsBadge && (
                    <span className="absolute -right-3 -top-2 rounded-full bg-rose-500 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-white">
                      {badgeText}
                    </span>
                  )}
                </div>
                <span className="mt-1 line-clamp-1 text-[10px] font-medium leading-tight">
                  {item.mobileLabel ?? item.label}
                </span>
              </motion.div>
              <span
                className={cn(
                  'absolute bottom-1 h-1 w-5 rounded-full bg-sky-300 transition-all duration-200',
                  isActive ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden="true"
              />
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
